'use client';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

type Props = {
	type: 'trade' | 'move' | 'drop' | 'add' | 'replace';
};

export function PlayerMovesSkeleton({ type }: Props) {
	const router = useRouter();
	const stepCount = type === 'move' ? 3 : 4;
	const steps = Array.from({ length: stepCount }, (_, i) => i);

	return (
		<div className="mx-auto max-w-screen-xl">
			<div className="border-b px-4 py-6 md:px-8">
				<h1 className="text-xl font-semibold">
					<Link href="" onClick={() => router.back()}>
						<FontAwesomeIcon icon={faChevronLeft} size="sm" className="mr-4" />
						Back
					</Link>
				</h1>
			</div>

			{/* Team Header Skeleton */}
			<div className="flex items-center gap-4 px-4 py-6 md:px-8">
				<div className="size-16 animate-pulse rounded-[10px] bg-gray-200" />
				<div className="h-6 w-48 animate-pulse rounded bg-gray-200" />
			</div>
			{(type === 'replace' || type === 'add') && (
				<div className="flex gap-4 flex-col px-4 py-6 md:px-8">
					<div className="max-w-[720px] border border-orange-200 bg-orange-50 p-4 rounded-md">
						<div className="h-6 w-24 mb-4 animate-pulse rounded bg-gray-200" />
						<div className="mb-2 h-4 w-32 animate-pulse rounded bg-gray-200" />
						<div className="mb-2 h-4 w-3/4 animate-pulse rounded bg-gray-200" />
						{type === 'add' && <div className="h-4 w-3/5 animate-pulse rounded bg-gray-200" />}
					</div>

					{type === 'add' && <div className="h-8 w-20 animate-pulse rounded bg-gray-200" />}
				</div>
			)}
			{(type === 'move' || type === 'drop') && (
				<div className="flex items-center gap-4 px-4 py-6 md:px-8">
					<div className="h-8 w-48 animate-pulse rounded bg-gray-200" />
				</div>
			)}

			{/* Steps Skeleton */}
			{(type === 'trade' || type === 'move') && (
				<div className="mx-auto hidden max-w-[720px] items-center justify-between px-8 pb-8 pt-4 lg:flex">
					{steps.map((_, index) => (
						<div key={`step-${index}`}>
							<div className="h-8 w-40 animate-pulse rounded bg-gray-200" />
						</div>
					))}
				</div>
			)}

			{/* Content Skeleton */}
			{type !== 'add' && type !== 'replace' && (
				<div className="flex flex-col gap-4 p-4 md:px-8">
					{type !== 'drop' && <div className="h-6 w-2/4 animate-pulse rounded bg-gray-200" />}
					<div
						className={clsx('h-6 w-3/4 animate-pulse rounded bg-gray-200 hidden lg:flex', {
							'sm:flex w-2/3': type === 'drop'
						})}
					/>
					<div className="flex gap-9 hidden lg:flex">
						<div className="w-[390px] h-[350px]">
							<div className="flex flex-col gap-4 rounded-lg border p-4">
								<div className="h-6 w-full animate-pulse rounded bg-gray-200" />
								{[1, 2].map((_, index) => (
									<div key={`preview-${index}`} className="h-24 w-full animate-pulse rounded bg-gray-200" />
								))}
							</div>
						</div>

						{/* Trade Preview Panel */}
						<div className="w-[390px] h-[350px]">
							<div className="flex flex-col gap-4 rounded-lg border p-4">
								<div className="h-6 w-full animate-pulse rounded bg-gray-200" />
								{[1, 2].map((_, index) => (
									<div key={`preview-${index}`} className="h-24 w-full animate-pulse rounded bg-gray-200" />
								))}
							</div>
						</div>
					</div>
					<div className="flex flex-wrap gap-4 lg:hidden">
						<div className="w-[160px] h-[150px rounded-lg border p-4 flex flex-col items-center justify-center gap-2">
							<div className="size-12 animate-pulse rounded-full bg-gray-200" />
							<div className="h-4 w-[120px] animate-pulse rounded bg-gray-200" />
							<div className="h-4 w-[80px] animate-pulse rounded bg-gray-200" />
							<div className="h-4 w-[100px] animate-pulse rounded bg-gray-200" />
						</div>
						<div className="w-[160px] h-[150px rounded-lg border p-4 flex flex-col items-center justify-center gap-2">
							<div className="size-12 animate-pulse rounded-full bg-gray-200" />
							<div className="h-4 w-[120px] animate-pulse rounded bg-gray-200" />
							<div className="h-4 w-[80px] animate-pulse rounded bg-gray-200" />
							<div className="h-4 w-[100px] animate-pulse rounded bg-gray-200" />
						</div>
						<div className="w-[160px] h-[150px rounded-lg border p-4 flex flex-col items-center justify-center gap-2">
							<div className="size-12 animate-pulse rounded-full bg-gray-200" />
							<div className="h-4 w-[120px] animate-pulse rounded bg-gray-200" />
							<div className="h-4 w-[80px] animate-pulse rounded bg-gray-200" />
							<div className="h-4 w-[100px] animate-pulse rounded bg-gray-200" />
						</div>
					</div>
				</div>
			)}

			{(type === 'replace' || type === 'add') && (
				<div className="flex flex-wrap gap-4 px-4 pb-6 md:px-8">
					<div className="w-[160px] h-[150px rounded-lg border p-4 flex flex-col items-center justify-center gap-2">
						<div className="size-12 animate-pulse rounded-full bg-gray-200" />
						<div className="h-4 w-[120px] animate-pulse rounded bg-gray-200" />
						<div className="h-4 w-[80px] animate-pulse rounded bg-gray-200" />
						<div className="h-4 w-[100px] animate-pulse rounded bg-gray-200" />
					</div>
					<div className="w-[160px] h-[150px rounded-lg border p-4 flex flex-col items-center justify-center gap-2">
						<div className="size-12 animate-pulse rounded-full bg-gray-200" />
						<div className="h-4 w-[120px] animate-pulse rounded bg-gray-200" />
						<div className="h-4 w-[80px] animate-pulse rounded bg-gray-200" />
						<div className="h-4 w-[100px] animate-pulse rounded bg-gray-200" />
					</div>
					<div className="w-[160px] h-[150px rounded-lg border p-4 flex flex-col items-center justify-center gap-2">
						<div className="size-12 animate-pulse rounded-full bg-gray-200" />
						<div className="h-4 w-[120px] animate-pulse rounded bg-gray-200" />
						<div className="h-4 w-[80px] animate-pulse rounded bg-gray-200" />
						<div className="h-4 w-[100px] animate-pulse rounded bg-gray-200" />
					</div>
				</div>
			)}

			{/* Bottom Buttons */}
			{type !== 'add' && (
				<div className="flex gap-2 px-4 md:px-8">
					<div className="h-8 w-20 animate-pulse rounded bg-gray-200" />
					<div className="h-8 w-20 animate-pulse rounded bg-gray-200" />
				</div>
			)}
		</div>
	);
}
